import React from "react";
import { Check, Close, ErrorOutline } from "@mui/icons-material";
import {
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
} from "@mui/material";
import { allRowsPassed, WorkflowValidationRow } from "./workflowValidation";

interface WorkflowValidationDialogProps {
  open: boolean;
  rows: WorkflowValidationRow[];
  /** The server half of the check (save + POST /definition/validate) is still in flight. */
  running: boolean;
  /** The server half failed (its error was already shown), so the checklist is not a verdict. */
  incomplete?: boolean;
  onClose: () => void;
}

/** The Validate checklist: one green/red row per rule, with the exact failure messages. */
const WorkflowValidationDialog: React.FC<WorkflowValidationDialogProps> = ({
  open,
  rows,
  running,
  incomplete = false,
  onClose,
}) => {
  const passed = allRowsPassed(rows);

  return (
    <Dialog open={open} onClose={onClose} fullWidth maxWidth="sm" aria-labelledby="validate-workflow-title">
      <DialogTitle id="validate-workflow-title">Validate workflow</DialogTitle>
      <IconButton aria-label="close" onClick={onClose} className="dialog-close-button">
        <Close />
      </IconButton>
      <DialogContent dividers>
        <Box className="workflow-builder__validation-card" component="ul" aria-label="Validation checks">
          {rows.map((row) => (
            <Box
              component="li"
              key={row.id}
              className={`workflow-builder__validation-row${
                row.passed ? "" : " workflow-builder__validation-row--failed"
              }`}
            >
              {row.passed ? <Check aria-label="Passed" /> : <ErrorOutline aria-label="Failed" />}
              <span>
                {row.label}
                {row.messages.map((message) => (
                  <span className="workflow-builder__validation-message" key={message}>
                    {message}
                  </span>
                ))}
              </span>
            </Box>
          ))}
        </Box>
        {running ? (
          <Box className="workflow-builder__validation-summary workflow-builder__validation-summary--running">
            <CircularProgress size={16} color="inherit" />
            Checking the saved draft…
          </Box>
        ) : incomplete ? (
          <Box className="workflow-builder__validation-summary workflow-builder__validation-summary--failed">
            <ErrorOutline />
            Couldn&apos;t check the saved draft. Fix the error that was reported, then validate again.
          </Box>
        ) : passed ? (
          <Box className="workflow-builder__validation-summary">
            <Check />
            All checks passed — you can publish this workflow.
          </Box>
        ) : (
          <Box className="workflow-builder__validation-summary workflow-builder__validation-summary--failed">
            <ErrorOutline />
            Fix the items in red, then validate again.
          </Box>
        )}
      </DialogContent>
      <DialogActions>
        <Button
          variant="contained"
          color="primary"
          size="small"
          /* eslint-disable-next-line jsx-a11y/no-autofocus -- dialogs.mdc requires autoFocus on
             the primary action; ConfirmationDialog opts out of this rule for the same reason. */
          autoFocus
          onClick={onClose}
        >
          Close
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default WorkflowValidationDialog;
